import { useLocation } from 'wouter'
import { Avatar } from '../../components/Avatar'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { useAuth } from '../../hooks/useAuth'
import { AppLayout } from '../../layouts/AppLayout'
import { ROUTES } from '../../routes'
import { styled } from '../../setup'
import { borders, colors, spacing, typography } from '../../theme'

const Container = styled('div')`
  max-width: 600px;
  margin: 0 auto;
`

const ProfileHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  margin-bottom: ${spacing['2xl']};
`

const ProfileInfo = styled('div')`
  flex: 1;
`

const DisplayName = styled('h1')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize2xl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.xs} 0;
`

const ProfileType = styled('span')<{ type: 'CHILD' | 'ADULT' }>`
  background: ${(props) =>
    props.type === 'CHILD' ? colors.successBg : 'rgba(59, 130, 246, 0.15)'};
  color: ${(props) => (props.type === 'CHILD' ? colors.success : '#2563eb')};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borders.radius};
  font-size: ${typography.fontSizeSm};
  font-weight: ${typography.fontWeightMedium};
  text-transform: uppercase;
`

const Section = styled('section')`
  margin-bottom: ${spacing['2xl']};
`

const SectionTitle = styled('h2')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  font-weight: ${typography.fontWeightSemibold};
  text-transform: uppercase;
  margin: 0 0 ${spacing.md} 0;
`

const LinkStatus = styled('div')<{ state: string }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.md};
  background: ${colors.bgSurfaceAlt};
  border: ${borders.width} solid ${colors.border};
  border-radius: ${borders.radiusLg};
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeBase};

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => {
      switch (props.state) {
        case 'LINKED':
          return colors.success
        case 'PENDING':
          return '#d97706'
        default:
          return colors.error
      }
    }};
  }
`

const AccountInfo = styled('p')`
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeBase};
  margin: 0;
`

const ButtonGroup = styled('div')`
  display: flex;
  gap: ${spacing.md};
  margin-top: ${spacing['2xl']};
`

export function ProfilePage() {
  const { profile, identity, isChild, isAdult, needsGuardian, logout } = useAuth()
  const [, setLocation] = useLocation()

  if (!profile.value) {
    return null
  }

  return (
    <AppLayout>
      <Container>
        <Card>
          <ProfileHeader>
            <Avatar name={profile.value.displayName} size={64} />
            <ProfileInfo>
              <DisplayName>{profile.value.displayName}</DisplayName>
              <ProfileType type={profile.value.type}>{profile.value.type}</ProfileType>
            </ProfileInfo>
          </ProfileHeader>

          {isChild.value && (
            <Section>
              <SectionTitle>Guardian Status</SectionTitle>
              <LinkStatus state={profile.value.childLinkState || 'UNLINKED'}>
                {profile.value.childLinkState === 'LINKED' && 'Connected to guardian'}
                {profile.value.childLinkState === 'PENDING' && 'Waiting for guardian approval'}
                {profile.value.childLinkState === 'UNLINKED' && 'No guardian linked'}
              </LinkStatus>

              {needsGuardian.value && (
                <Button
                  variant="primary"
                  onClick={() => setLocation(ROUTES.CHILD_LINK_REQUEST)}
                  style={{ marginTop: spacing.md }}
                >
                  {profile.value.childLinkState === 'PENDING' ? 'Check Status' : 'Link a Guardian'}
                </Button>
              )}
            </Section>
          )}

          {isAdult.value && (
            <Section>
              <Button variant="primary" onClick={() => setLocation(ROUTES.GUARDIAN_DASHBOARD)}>
                Guardian Dashboard
              </Button>
            </Section>
          )}

          <Section>
            <SectionTitle>Account</SectionTitle>
            <AccountInfo>Email: {identity.value?.email || 'Not set'}</AccountInfo>
          </Section>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setLocation(ROUTES.SETTINGS)}>
              Settings
            </Button>
            <Button variant="danger" onClick={() => logout()}>
              Log Out
            </Button>
          </ButtonGroup>
        </Card>
      </Container>
    </AppLayout>
  )
}
